import { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  FileText,
  Network,
  Plus,
  AlertCircle,
  Clock,
  MoreHorizontal,
  Trash2,
  Edit3,
  ExternalLink,
  Copy,
  Bookmark,
  Map as MapIcon,
  GitBranch,
  Target,
  Lightbulb,
} from 'lucide-react';
import { docsApi, type Doc, type DocType } from '../api/docs';
import type { Member } from '../api/members';
import { usersApi } from '../api/users';
import { toast, useToastStore } from '../components/Toast';
import { formatRelativeTime, getInitials, getCollaboratorColor } from '../lib/utils';

interface CreateDocState {
  title: string;
  icon: string;
  type: DocType;
}

interface ContextMenuState {
  docId: string;
  x: number;
  y: number;
}

const DOC_ICON_CHOICES = ['FileText', 'Edit3', 'File', 'FileSpreadsheet', 'Bookmark'];
const MAP_ICON_CHOICES = ['Network', 'Map', 'GitBranch', 'Target', 'Lightbulb'];

export function DocListPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: docs,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['docs', wsId],
    queryFn: () => docsApi.list(wsId),
    enabled: !!wsId,
  });

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: usersApi.me,
  });

  const { data: members } = useQuery({
    queryKey: ['members', wsId],
    queryFn: () => import('../api/members').then((m) => m.membersApi.list(wsId)),
    enabled: !!wsId,
  });

  // 创建弹窗
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<CreateDocState>({ title: '', icon: 'FileText', type: 'doc' });
  const [submitError, setSubmitError] = useState<string | null>(null);

  // 右键菜单
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // 删除确认弹窗
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState<Doc | null>(null);

  // 重命名弹窗
  const [renameDoc, setRenameDoc] = useState<Doc | null>(null);
  const [renameTitle, setRenameTitle] = useState('');

  const createMutation = useMutation({
    mutationFn: () =>
      docsApi.create(wsId, {
        title: form.title.trim() || (form.type === 'doc' ? '未命名文档' : '未命名导图'),
        emoji: form.icon,
        type: form.type,
      }),
    onSuccess: (doc) => {
      queryClient.invalidateQueries({ queryKey: ['docs', wsId] });
      toast.success(form.type === 'doc' ? '文档已创建' : '思维导图已创建');
      setOpen(false);
      setForm({ title: '', icon: 'FileText', type: 'doc' });
      setSubmitError(null);
      if (form.type === 'doc') {
        navigate(`/space/${wsId}/docs/${doc.id}`);
      } else {
        navigate(`/space/${wsId}/mindmaps/${doc.id}`);
      }
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : '创建失败';
      setSubmitError(msg);
      toast.error(msg, '创建失败');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (docId: string) => docsApi.remove(docId),
    onSuccess: (_data, docId) => {
      queryClient.invalidateQueries({ queryKey: ['docs', wsId] });
      setDeleteConfirmDoc(null);
      const restoredDocId = docId;
      useToastStore.getState().push({
        description: '已删除',
        variant: 'success',
        duration: 5000,
        action: {
          label: '撤销',
          onClick: () => {
            docsApi.restore(wsId, restoredDocId).then(() => {
              queryClient.invalidateQueries({ queryKey: ['docs', wsId] });
              toast.success('已恢复');
            });
          },
        },
      });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : '删除失败';
      toast.error(msg, '删除失败');
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ docId, title }: { docId: string; title: string }) =>
      docsApi.update(docId, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['docs', wsId] });
      setRenameDoc(null);
      toast.success('已重命名');
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : '重命名失败';
      toast.error(msg, '重命名失败');
    },
  });

  function openCreateDialog(type: DocType) {
    setForm({
      title: '',
      icon: type === 'doc' ? DOC_ICON_CHOICES[0] : MAP_ICON_CHOICES[0],
      type,
    });
    setSubmitError(null);
    setOpen(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    createMutation.mutate();
  }

  function handleContextMenu(e: React.MouseEvent, docId: string) {
    e.preventDefault();
    e.stopPropagation();
    const x = Math.min(e.clientX, window.innerWidth - 200);
    const y = Math.min(e.clientY, window.innerHeight - 180);
    setContextMenu({ docId, x, y });
  }

  function closeContextMenu() {
    setContextMenu(null);
  }

  function handleOpenDoc(doc: Doc) {
    closeContextMenu();
    const targetPath =
      doc.type === 'doc'
        ? `/space/${wsId}/docs/${doc.id}`
        : `/space/${wsId}/mindmaps/${doc.id}`;
    navigate(targetPath);
  }

  function handleRenameStart(doc: Doc) {
    closeContextMenu();
    setRenameTitle(doc.title || '');
    setRenameDoc(doc);
  }

  function handleRenameSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!renameDoc) return;
    const title = renameTitle.trim();
    if (!title) return;
    if (title === renameDoc.title) {
      setRenameDoc(null);
      return;
    }
    renameMutation.mutate({ docId: renameDoc.id, title });
  }

  function handleDeleteConfirm(doc: Doc) {
    closeContextMenu();
    setDeleteConfirmDoc(doc);
  }

  function handleDeleteExecute() {
    if (!deleteConfirmDoc) return;
    deleteMutation.mutate(deleteConfirmDoc.id);
  }

  function handleCopyLink(doc: Doc) {
    closeContextMenu();
    const targetPath =
      doc.type === 'doc'
        ? `/space/${wsId}/docs/${doc.id}`
        : `/space/${wsId}/mindmaps/${doc.id}`;
    navigator.clipboard.writeText(`${window.location.origin}${targetPath}`).then(
      () => toast.success('链接已复制'),
      () => toast.error('复制失败'),
    );
  }

  // 点击外部关闭右键菜单
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        closeContextMenu();
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') closeContextMenu();
    }
    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [contextMenu]);

  const memberMap = new globalThis.Map<string, Member>();
  const membersArr = (members as Member[] | undefined) ?? [];
  membersArr.forEach((m) => memberMap.set(m.id, m));

  return (
    <div className="mx-auto max-w-5xl px-8 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#1a1a2e]">文档与导图</h1>
          <p className="mt-1 text-sm text-text-secondary">空间内的所有文档和思维导图</p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn btn-secondary btn-md" onClick={() => openCreateDialog('mindmap')}>
            <Network className="h-4 w-4" /> 新建思维导图
          </button>
          <button type="button" className="btn btn-primary btn-md" onClick={() => openCreateDialog('doc')}>
            <Plus className="h-4 w-4" /> 新建文档
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="card flex flex-col gap-3 p-5">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 shrink-0 animate-shimmer rounded-lg bg-bg-subtle" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 animate-shimmer rounded bg-bg-subtle" />
                  <div className="h-3 w-1/3 animate-shimmer rounded bg-bg-subtle" />
                </div>
              </div>
              <div className="flex items-center justify-between border-t border-border/60 pt-2">
                <div className="h-3 w-20 animate-shimmer rounded bg-bg-subtle" />
                <div className="h-3 w-12 animate-shimmer rounded bg-bg-subtle" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="mt-8 flex flex-col items-center rounded-xl bg-bg-surface p-10 text-center ring-1 ring-black/5 shadow-sm">
          <AlertCircle className="h-8 w-8 text-danger" />
          <p className="mt-3 text-sm text-text-secondary">
            {error instanceof Error ? error.message : '加载失败'}
          </p>
          <button
            type="button"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['docs', wsId] })}
            className="btn btn-secondary btn-md mt-4"
          >
            重试
          </button>
        </div>
      ) : docs && docs.length > 0 ? (
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {docs.map((doc) => {
            const editor = doc.lastEditedBy ? memberMap.get(doc.lastEditedBy) : undefined;
            const editorName =
              editor?.nickname ||
              editor?.email?.split('@')[0] ||
              (doc.lastEditedBy === me?.id ? '我' : '未知');
            const isMe = doc.lastEditedBy && doc.lastEditedBy === me?.id;
            const targetPath =
              doc.type === 'doc'
                ? `/space/${wsId}/docs/${doc.id}`
                : `/space/${wsId}/mindmaps/${doc.id}`;
            return (
              <div
                key={doc.id}
                className="card group relative flex flex-col gap-3 p-5 text-left"
              >
                <button
                  type="button"
                  onClick={() => navigate(targetPath)}
                  onContextMenu={(e) => handleContextMenu(e, doc.id)}
                  className="flex flex-1 flex-col gap-3 text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-accent-50 to-accent-100 shadow-sm ring-1 ring-accent/20">
                      {doc.emoji ? (
                        <span className="text-lg">{doc.emoji}</span>
                      ) : doc.type === 'doc' ? (
                        <FileText className="h-5 w-5 text-accent" />
                      ) : (
                        <Network className="h-5 w-5 text-accent" />
                      )}
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <div className="truncate text-sm font-medium text-[#1a1a2e]">{doc.title || '未命名'}</div>
                      <div className="mt-0.5 flex items-center gap-1 text-xs text-text-tertiary">
                        {doc.type === 'doc' ? (
                          <FileText className="h-3 w-3" />
                        ) : (
                          <Network className="h-3 w-3" />
                        )}
                        {doc.type === 'doc' ? '文档' : '思维导图'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between border-t border-border/60 pt-2 text-xs text-text-tertiary">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatRelativeTime(doc.updatedAt)}
                    </span>
                    {editor && (
                      <span className="flex items-center gap-1">
                        <span
                          className="flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-medium text-white"
                          style={{
                            backgroundColor: getCollaboratorColor(
                              doc.lastEditedBy ?? editor.id,
                            ),
                          }}
                          aria-hidden
                        >
                          {getInitials(isMe ? '我' : editorName)}
                        </span>
                        {isMe ? '我' : editorName}
                      </span>
                    )}
                  </div>
                </button>
                {/* 操作按钮（hover 显示） */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleContextMenu(e as unknown as React.MouseEvent, doc.id);
                  }}
                  className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-lg text-text-tertiary opacity-0 transition-opacity hover:bg-bg-subtle hover:text-text-primary group-hover:opacity-100"
                  aria-label="更多操作"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-12 flex flex-col items-center text-center">
          <svg
            width="120"
            height="120"
            viewBox="0 0 120 120"
            fill="none"
            className="mb-4"
            aria-hidden="true"
          >
            <rect x="20" y="15" width="80" height="90" rx="8" className="fill-accent-50" />
            <rect x="35" y="35" width="50" height="6" rx="3" className="fill-accent-200" />
            <rect x="35" y="49" width="40" height="6" rx="3" className="fill-accent-100" />
            <rect x="35" y="63" width="45" height="6" rx="3" className="fill-accent-100" />
            <circle cx="60" cy="88" r="12" className="fill-accent" />
            <path
              d="M55 88l4 4 6-7"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="text-base font-medium text-[#1a1a2e]">还没有任何文档</p>
          <p className="mt-1 text-sm text-text-secondary">创建你的第一个文档或思维导图，开启协作</p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              className="btn btn-secondary btn-md"
              onClick={() => openCreateDialog('mindmap')}
            >
              <Network className="h-4 w-4" /> 新建思维导图
            </button>
            <button
              type="button"
              className="btn btn-primary btn-md"
              onClick={() => openCreateDialog('doc')}
            >
              <Plus className="h-4 w-4" /> 新建文档
            </button>
          </div>
        </div>
      )}

      {/* 右键菜单 */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="fixed z-50 w-44 rounded-lg border border-border bg-bg-surface py-1 shadow-lg"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-bg-subtle"
            onClick={() => {
              const doc = docs?.find((d) => d.id === contextMenu.docId);
              if (doc) handleOpenDoc(doc);
            }}
          >
            <ExternalLink className="h-4 w-4" />
            打开
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-bg-subtle"
            onClick={() => {
              const doc = docs?.find((d) => d.id === contextMenu.docId);
              if (doc) handleRenameStart(doc);
            }}
          >
            <Edit3 className="h-4 w-4" />
            重命名
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-bg-subtle"
            onClick={() => {
              const doc = docs?.find((d) => d.id === contextMenu.docId);
              if (doc) handleCopyLink(doc);
            }}
          >
            <Copy className="h-4 w-4" />
            复制链接
          </button>
          <div className="my-1 border-t border-border" />
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger transition-colors hover:bg-[#fef2f2]"
            onClick={() => {
              const doc = docs?.find((d) => d.id === contextMenu.docId);
              if (doc) handleDeleteConfirm(doc);
            }}
          >
            <Trash2 className="h-4 w-4" />
            删除
          </button>
        </div>
      )}

      {/* 创建弹窗 */}
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-bg-overlay backdrop-blur-sm animate-fade-in" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[480px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-bg-surface p-6 shadow-xl ring-1 ring-black/5"
            aria-describedby={undefined}
          >
            <Dialog.Title className="text-lg font-semibold tracking-tight text-[#1a1a2e]">
              {form.type === 'doc' ? '新建文档' : '新建思维导图'}
            </Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-text-secondary">
              {form.type === 'doc'
                ? '创建后会进入富文本编辑器。'
                : '创建后会进入思维导图编辑器。'}
            </Dialog.Description>

            <form onSubmit={handleSubmit} className="mt-5 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="doc-title">
                  标题
                </label>
                <input
                  id="doc-title"
                  type="text"
                  maxLength={200}
                  autoFocus
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  className="input"
                  placeholder={form.type === 'doc' ? '未命名文档' : '未命名导图'}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">图标</label>
                <div className="flex flex-wrap gap-2">
                  {(form.type === 'doc' ? DOC_ICON_CHOICES : MAP_ICON_CHOICES).map((iconName) => {
                    const IconComponent = iconName === 'FileText' ? FileText : 
                      iconName === 'Edit3' ? Edit3 :
                      iconName === 'File' ? FileText :
                      iconName === 'FileSpreadsheet' ? FileText :
                      iconName === 'Bookmark' ? Bookmark :
                      iconName === 'Network' ? Network :
                      iconName === 'Map' ? MapIcon :
                      iconName === 'GitBranch' ? GitBranch :
                      iconName === 'Target' ? Target :
                      iconName === 'Lightbulb' ? Lightbulb : FileText;
                    return (
                      <button
                        key={iconName}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, icon: iconName }))}
                        className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${
                          form.icon === iconName
                            ? 'border-accent bg-accent-50 text-accent'
                            : 'border-border text-text-secondary hover:bg-bg-subtle hover:text-text-primary'
                        }`}
                        aria-pressed={form.icon === iconName}
                        aria-label={`选择图标 ${iconName}`}
                      >
                        <IconComponent className="h-4 w-4" />
                      </button>
                    );
                  })}
                </div>
              </div>

              {submitError && (
                <div className="flex items-center gap-2 rounded-md bg-[#fef2f2] px-3 py-2 text-sm text-danger">
                  <AlertCircle className="h-4 w-4" />
                  {submitError}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="btn btn-ghost btn-md">
                  取消
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="btn btn-primary btn-md"
                >
                  {createMutation.isPending ? '创建中...' : '创建'}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* 删除确认弹窗 */}
      <Dialog.Root
        open={!!deleteConfirmDoc}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirmDoc(null);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-bg-overlay backdrop-blur-sm animate-fade-in" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[420px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-bg-surface p-6 shadow-xl ring-1 ring-black/5"
            aria-describedby={undefined}
          >
            <Dialog.Title className="text-lg font-semibold">确认删除</Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-text-secondary">
              确定要删除「{deleteConfirmDoc?.title || '未命名'}」吗？删除后可以
              <span className="font-medium">撤销</span>恢复。
            </Dialog.Description>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmDoc(null)}
                className="btn btn-ghost btn-md"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleDeleteExecute}
                disabled={deleteMutation.isPending}
                className="btn btn-danger btn-md"
              >
                {deleteMutation.isPending ? '删除中...' : '删除'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* 重命名弹窗 */}
      <Dialog.Root
        open={!!renameDoc}
        onOpenChange={(open) => {
          if (!open) setRenameDoc(null);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-bg-overlay backdrop-blur-sm animate-fade-in" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[480px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-bg-surface p-6 shadow-xl ring-1 ring-black/5"
            aria-describedby={undefined}
          >
            <Dialog.Title className="text-lg font-semibold">重命名</Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-text-secondary">
              输入新的标题
            </Dialog.Description>

            <form onSubmit={handleRenameSubmit} className="mt-5">
              <input
                type="text"
                maxLength={200}
                autoFocus
                value={renameTitle}
                onChange={(e) => setRenameTitle(e.target.value)}
                className="input"
                placeholder="输入标题"
              />
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setRenameDoc(null)}
                  className="btn btn-ghost btn-md"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={renameMutation.isPending || !renameTitle.trim()}
                  className="btn btn-primary btn-md"
                >
                  {renameMutation.isPending ? '保存中...' : '保存'}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}